import { useEffect, useState } from "react";
import type { Countdown } from "./countdown.types";
import "./CountdownEditor.css";

type Props = {
  open: boolean;
  countdown?: Countdown | null;
  onClose: () => void;
  onSave: (countdown: Countdown) => void;
};

const categories = [
  "Personal",
  "Travel",
  "Birthday",
  "Festival",
  "Study",
  "Work",
  "Event Circle",
];

function toInputDateTime(value?: string): string {
  if (!value) {
    const date = new Date();
    date.setHours(date.getHours() + 1, 0, 0, 0);

    return date.toISOString().slice(0, 16);
  }

  return new Date(value).toISOString().slice(0, 16);
}

export function CountdownEditor({
  open,
  countdown,
  onClose,
  onSave,
}: Props) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Personal");
  const [eventAt, setEventAt] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [journeyEnabled, setJourneyEnabled] = useState(false);
  const [error, setError] = useState("");
  const [gettingLocation, setGettingLocation] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setTitle(countdown?.title ?? "");
    setCategory(countdown?.category ?? "Personal");
    setEventAt(toInputDateTime(countdown?.eventAt));
    setLocation(countdown?.location ?? "");
    setNotes(countdown?.notes ?? "");
    setJourneyEnabled(countdown?.journeyEnabled ?? false);
    setError("");
  }, [open, countdown]);

  if (!open) {
    return null;
  }

  function useCurrentLocation() {
    if (!navigator.geolocation) {
      setError(
        "This browser does not support current location.",
      );

      return;
    }

    setGettingLocation(true);
    setError("");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation(
          `${position.coords.latitude.toFixed(5)}, ` +
            `${position.coords.longitude.toFixed(5)}`,
        );

        setGettingLocation(false);
      },
      () => {
        setError(
          "Current location was not allowed. You can type the place manually.",
        );

        setGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10_000,
      },
    );
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();

    if (!title.trim()) {
      setError("Countdown title is required.");
      return;
    }

    const target = new Date(eventAt);

    if (
      Number.isNaN(target.getTime()) ||
      target.getTime() <= Date.now()
    ) {
      setError("Choose a future date and time.");
      return;
    }

    const now = Date.now();

    onSave({
      id: countdown?.id ?? crypto.randomUUID(),
      cloudId: countdown?.cloudId,
      title: title.trim(),
      category,
      eventAt: target.toISOString(),
      location: location.trim(),
      notes: notes.trim(),
      journeyEnabled,
      createdAt: countdown?.createdAt ?? now,
      updatedAt: now,
    });
  }

  return (
    <div
      className="countdown-editor__backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="countdown-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="countdown-editor-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="countdown-editor__header">
          <div>
            <span>
              {countdown ? "EDIT COUNTDOWN" : "NEW COUNTDOWN"}
            </span>

            <h2 id="countdown-editor-title">
              {countdown ? "Edit countdown" : "Add countdown"}
            </h2>
          </div>

          <button
            className="countdown-editor__close"
            type="button"
            aria-label="Close countdown editor"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <form onSubmit={submit}>
          <label className="countdown-editor__field">
            <span>Countdown title</span>

            <input
              autoFocus
              maxLength={80}
              placeholder="Example: Mayapur journey"
              value={title}
              onChange={(event) =>
                setTitle(event.target.value)
              }
            />
          </label>

          <label className="countdown-editor__field">
            <span>Category</span>

            <select
              value={category}
              onChange={(event) =>
                setCategory(event.target.value)
              }
            >
              {categories.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label className="countdown-editor__field">
            <span>Target date and time</span>

            <input
              type="datetime-local"
              value={eventAt}
              onChange={(event) =>
                setEventAt(event.target.value)
              }
            />
          </label>

          <label className="countdown-editor__field">
            <span>Location</span>

            <div className="countdown-editor__location-row">
              <input
                maxLength={160}
                placeholder="Search or type a place"
                value={location}
                onChange={(event) =>
                  setLocation(event.target.value)
                }
              />

              <button
                type="button"
                onClick={useCurrentLocation}
                disabled={gettingLocation}
              >
                {gettingLocation ? "Finding…" : "Use current"}
              </button>
            </div>
          </label>

          <label className="countdown-editor__field">
            <span>Notes</span>

            <textarea
              maxLength={500}
              placeholder="Optional details"
              rows={3}
              value={notes}
              onChange={(event) =>
                setNotes(event.target.value)
              }
            />
          </label>

          <label className="countdown-editor__journey">
            <input
              type="checkbox"
              checked={journeyEnabled}
              onChange={(event) =>
                setJourneyEnabled(event.target.checked)
              }
            />

            <span>
              <strong>Enable Event Journey</strong>
              <small>
                Show this countdown in your journey planning view.
              </small>
            </span>
          </label>

          {error && (
            <p className="countdown-editor__error">
              {error}
            </p>
          )}

          <footer className="countdown-editor__actions">
            <button
              className="countdown-editor__cancel"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              className="countdown-editor__save"
              type="submit"
            >
              {countdown ? "Save changes" : "Add countdown"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}